// =============================================================================
// operational-view-client.ts — Client API du read-model A-15 (BC analytics), DOUBLABLE.
// =============================================================================
// Miroir des endpoints ADMIN de S14/D1 (apps/api/src/analytics/operational-view-admin.controller.ts,
// AdminSessionGuard). Comme les autres clients UI (campaign, suppliers…), expose une INTERFACE
// (OperationalViewClient) consommée par les hooks TanStack ; l'impl HTTP réelle (fetch) vient
// derrière la MÊME interface sans toucher l'UI (DI seam, stub-first).
//
// DTO = miroirs HTTP (PAS le Published Language, PAS @jeko/db) : l'enum de séniorité est répliqué
// en union de littéraux ASCII (parité Prisma RateSeniorityLevel, cf. campaign-client.ts) — la SPA
// ne couple PAS au paquet DB ; les accents sont de l'i18n front (operational-view-labels.ts).
//
// AUTORITÉ = API (G0-S14-7a) : MIN/MAX/médiane/moyenne/écart-type, flag Hors Jeux, bandes percentile,
// compteurs, écarts et évolution sont calculés AU VOL côté API. L'UI ne porte AUCUNE règle métier : elle
// LIT et habille (libellé/transparence). Le campaignId est TOUJOURS la ressource d'URL. Vue NOMINATIVE
// (admin) — PAS de K-anonymity (G0-S14-7b). S14-bis (§2.5 v2.2) : le modèle 4 codes couleurs Admin a été
// RETIRÉ (refonte Pages Profils & Fournisseurs).
//
//   GET /admin/operational-view/:campaignId/cards           → OperationalCardsDto
//   GET /admin/operational-view/:campaignId/profile-matrix  → ProfileMatrixResponseDto
//   GET /admin/operational-view/:campaignId/supplier-list   → SupplierListResponseDto
//   GET /admin/operational-view/:campaignId/profile-detail  → ProfileDetailResponseDto
//   GET /admin/operational-view/:campaignId/supplier-detail → SupplierDetailResponseDto
//
// (F) FILTRES SERVEUR communs (query) : cycleNumber? (entier > 0), profileCode? (string),
//     seniorityLevel? (enum, fail-closed côté API), supplierSiret? (string). Sérialisés par l'adaptateur.

// --- Enum miroir HTTP (parité Prisma RateSeniorityLevel, ASCII — accents = i18n front) ---

import { HttpApiError } from '@/api/http-error';
import type { CampaignStateDto, CycleStateDto } from '@/api/campaign-client';

/** Niveau de séniorité d'un profil (parité Prisma RateSeniorityLevel). */
export type RateSeniorityLevelDto =
  | 'Debutant'
  | 'Maitrisant'
  | 'Expert'
  | 'Referent';

// =============================================================================
// S14-bis §2.5 v2.2 — Pages Profils & Fournisseurs (miroirs HTTP des DTO back analytics). Tout est calculé
// AU VOL côté API sur la DERNIÈRE RÉPONSE ; l'UI LIT et habille (aucune règle métier ici). Les bandes
// percentile (interne) ne sont JAMAIS des colonnes — seuls leurs COMPTEURS arrivent au front.
// =============================================================================

/** Section 1 — cards d'indicateurs communs (Profils & Fournisseurs). Miroir OperationalCardsDto. */
export interface OperationalCardsDto {
  readonly campaignId: string;
  readonly cycleNumber: number | null;
  /** Fournisseurs invités à la campagne. */
  readonly invitedSupplierCount: number;
  /** X : couples (fournisseur × Profil×Niveau) ayant un TJM (dernière réponse). */
  readonly collectedCellCount: number;
  /** Y : total attendu = invités × couples du périmètre. */
  readonly expectedCellCount: number;
  /** TJM hors jeu : nb de couples Profil×Niveau ayant ≥ 1 TJM Hors Jeux. */
  readonly horsJeuxCoupleCount: number;
  /** Avancement DU CYCLE : réponses du cycle courant / attendu (%). Distinct de collectedCellCount. */
  readonly cycleProgressPct: number;
}

/** Page Profils §2 — une ligne de la matrice Profil × Niveau. Miroir ProfileMatrixRowDto. */
export interface ProfileMatrixRowDto {
  readonly profileCode: string;
  readonly seniorityLevel: RateSeniorityLevelDto;
  /** Famille métier du profil (catalog figé, DBU §2.5 l.1071). `null` si le profil est absent du catalogue. */
  readonly family: string | null;
  readonly count: number;
  readonly minEur: number | null;
  readonly maxEur: number | null;
  readonly avgEur: number | null;
  readonly medianEur: number | null;
  readonly stddevEur: number | null;
  readonly targetPriceEur: number | null;
  /** Taux de réponse = fournisseurs ayant répondu pour ce couple / invités (%). */
  readonly responseRatePct: number;
  /** Colonne « Codes » 1 : # fournisseurs Hors Jeux. */
  readonly horsJeuxSupplierCount: number;
  /** Colonne « Codes » 2 : # fournisseurs > P75 (bande interne). */
  readonly aboveP75SupplierCount: number;
  /** Warning « cycle non actualisé » : ≥ 1 fournisseur retenu sur un cycle < cycle ouvert. */
  readonly cycleStale: boolean;
}

/** Réponse page Profils §2 (matrice). Lignes = couples du PÉRIMÈTRE. Miroir ProfileMatrixResponseDto. */
export interface ProfileMatrixResponseDto {
  readonly campaignId: string;
  readonly cycleNumber: number | null;
  readonly rows: readonly ProfileMatrixRowDto[];
}

/** Page Fournisseurs §2 — une ligne par fournisseur invité. Miroir SupplierRowDto. */
export interface SupplierRowDto {
  readonly supplierSiret: string;
  /** Raison sociale (ACL supplier-reference) ; `''` si introuvable. */
  readonly raisonSociale: string;
  /** Taux de réponse = couples remplis / couples du périmètre (%). */
  readonly responseRatePct: number;
  /** # profils Hors Jeu. */
  readonly horsJeuxProfileCount: number;
  /** # profils < P25 (bande interne). */
  readonly belowP25Count: number;
  /** # profils [P25-P75]. */
  readonly withinP25P75Count: number;
  /** # profils > P75. */
  readonly aboveP75Count: number;
  /** Date de réponse (ISO 8601) ; null si aucune réponse. */
  readonly lastResponseAt: string | null;
  /** Warning « cycle non actualisé ». */
  readonly cycleStale: boolean;
}

/** Réponse page Fournisseurs §2 (liste). Lignes = TOUS les invités. Miroir SupplierListResponseDto. */
export interface SupplierListResponseDto {
  readonly campaignId: string;
  readonly cycleNumber: number | null;
  readonly rows: readonly SupplierRowDto[];
}

/**
 * Section 3 (détail dynamique) — une ligne (fournisseur × couple) commune aux deux pages. Miroir
 * OperationalDetailRowDto. Écarts/évolution calculés AU VOL côté API (base = dernière réponse).
 */
export interface OperationalDetailRowDto {
  readonly supplierSiret: string;
  readonly raisonSociale: string;
  readonly profileCode: string;
  readonly seniorityLevel: RateSeniorityLevelDto;
  readonly responseC1Eur: number | null;
  readonly responseC2Eur: number | null;
  readonly retainedEur: number | null;
  readonly medianEur: number | null;
  readonly respondentCount: number;
  readonly targetPriceEur: number | null;
  readonly ecartTargetEur: number | null;
  readonly ecartTargetPct: number | null;
  readonly ecartMedianEur: number | null;
  readonly ecartMedianPct: number | null;
  readonly evolutionC1C2Eur: number | null;
  readonly evolutionC1C2Pct: number | null;
  /** Hors Jeux — mise en évidence transparence + signal NON-VISUEL doublé (Cx-3). */
  readonly horsJeux: boolean;
}

/** Réponse page Profils §3 : détail d'UN couple. Miroir ProfileDetailResponseDto. */
export interface ProfileDetailResponseDto {
  readonly campaignId: string;
  readonly profileCode: string | null;
  readonly seniorityLevel: RateSeniorityLevelDto | null;
  readonly rows: readonly OperationalDetailRowDto[];
}

/** Réponse page Fournisseurs §3 : détail d'UN fournisseur. Miroir SupplierDetailResponseDto. */
export interface SupplierDetailResponseDto {
  readonly campaignId: string;
  readonly supplierSiret: string | null;
  readonly raisonSociale: string;
  readonly rows: readonly OperationalDetailRowDto[];
}

// =============================================================================
// §2.5 « Suivi de campagne temps réel » (concern A4) — santé/alertes de pilotage. Miroir HTTP du
// read-model D+E (CampaignHealthDto) déjà exposé par l'API (GET .../:campaignId/health). Juxtaposition
// de KPI (JAMAIS un score) + alertes proactives composite (date ∧ participation), in-app, non intrusives
// (DBU §1.4.9 : aucun push, aucun email sortant admin). Calcul AU VOL côté API (G0-S14-7a).
// =============================================================================

/** Sévérité d'une alerte de pilotage admin (miroir back AlertSeverity). */
export type AlertSeverityDto = 'warning' | 'critical';

/** Une alerte de pilotage admin in-app (miroir AdminAlertDto). Non intrusive (DBU §1.4.9). */
export interface AdminAlertDto {
  readonly severity: AlertSeverityDto;
  /** Seuil de jours-avant-échéance déclencheur (J-N) du paramètre. */
  readonly daysBeforeThreshold: number;
  /** Seuil de participation (%) déclencheur du paramètre. */
  readonly participationPctThreshold: number;
  /** Jours restants avant l'échéance du cycle courant (négatif si dépassée). */
  readonly daysRemaining: number;
  /** % de participation observé AU VOL. */
  readonly participationPct: number;
  /** Libellé non intrusif (avertissement / critique). */
  readonly message: string;
}

/**
 * Santé/suivi OPP-02 = JUXTAPOSITION de KPI au vol (participation, invités/soumis, Hors Jeux) + alertes
 * composite. JAMAIS un score agrégé (G0-S14-6). Miroir CampaignHealthDto.
 */
export interface CampaignHealthDto {
  readonly campaignId: string;
  readonly cycleNumber: number | null;
  /** % de participation AU VOL (submitted / invited). */
  readonly participationPct: number;
  readonly submittedSupplierCount: number;
  readonly invitedSupplierCount: number;
  /** Nombre de cellules Hors Jeux (juxtaposé, jamais agrégé en score). */
  readonly horsJeuxCount: number;
  /** Alertes composite actives (peut être vide). */
  readonly alerts: readonly AdminAlertDto[];
}

// =============================================================================
// V-DASH — Tableau de bord MACROSCOPIQUE Campagnes A-02 (miroir HTTP du read-model back analytics).
// GET /admin/dashboard/campaigns → CampaignsDashboardResponseDto : synthèse + registre enrichi + criticité
// en UN round-trip (PAS de fan-out N+1). Criticité R/O/V dérivée serveur de computeAlerts. AUTORITÉ = API.
// =============================================================================

/** Criticité par ligne (miroir DashboardCriticality) : R (critique) / O (warning) / V (aucune alerte). */
export type DashboardCriticalityDto = 'R' | 'O' | 'V';

/** Un cycle projeté pour le registre : échéance + jours restants (calculés AU VOL côté API). */
export interface DashboardCycleDto {
  readonly cycleNumber: number;
  readonly state: CycleStateDto;
  /** Échéance (close_at, ISO 8601) ; `null` si non planifié. */
  readonly closeAt: string | null;
  /** Jours restants avant l'échéance (négatif si dépassée) ; `null` si `closeAt` nul. */
  readonly daysRemaining: number | null;
}

/** Une ligne du registre macroscopique (une campagne). Volumétrie NON nominative (comptes). */
export interface DashboardCampaignRowDto {
  readonly campaignId: string;
  readonly intitule: string;
  readonly state: CampaignStateDto;
  readonly createdAt: string;
  readonly invitedSupplierCount: number;
  readonly profileCount: number;
  readonly cycles: readonly DashboardCycleDto[];
  readonly currentCycleNumber: number | null;
  readonly participationPct: number;
  readonly submittedSupplierCount: number;
  readonly criticality: DashboardCriticalityDto;
  /** Alertes composite actives (deadline ∧ participation) — cliquables côté UI (redirection campagne). */
  readonly alerts: readonly AdminAlertDto[];
}

/** Synthèse Zone 1 : compteurs par bucket de statut opérationnel + ratio brouillon (miroir back). */
export interface CampaignsDashboardSummaryDto {
  readonly totalCount: number;
  readonly openCount: number;
  readonly draftCount: number;
  readonly closedCount: number;
  readonly cancelledCount: number;
  /** Ratio brouillon = round(brouillon / total × 100) ; 0 si aucune campagne. */
  readonly draftRatioPct: number;
}

/** Réponse UNIQUE du tableau de bord macroscopique (synthèse + registre enrichi + criticité). */
export interface CampaignsDashboardResponseDto {
  readonly summary: CampaignsDashboardSummaryDto;
  readonly campaigns: readonly DashboardCampaignRowDto[];
}

// --- Filtres serveur (F) ------------------------------------------------------

/** Filtres serveur communs (F). Tous optionnels ; l'API valide (fail-closed). */
export interface OperationalViewFilters {
  readonly cycleNumber?: number;
  readonly profileCode?: string;
  readonly seniorityLevel?: RateSeniorityLevelDto;
  /** Restriction à UN fournisseur (Section 3 page Fournisseurs). */
  readonly supplierSiret?: string;
}

/**
 * Erreur applicative renvoyée par l'API admin operational-view (HTTP 4xx/5xx). L'UI l'affiche
 * telle quelle ; elle ne réinterprète JAMAIS la règle métier (autorité = API).
 */
export class OperationalViewApiError extends HttpApiError {
  public constructor(message: string, status?: number) {
    super(message, status, 'OperationalViewApiError');
  }
}

/**
 * Contrat du client read-model ADMIN §2.5 v2.2 (AdminSessionGuard) — miroir des 5 GET Pages Profils &
 * Fournisseurs. Aucune règle métier ici : stats, flag Hors Jeux, bandes, compteurs, écarts et évolution
 * sont l'autorité de l'API (calcul AU VOL, G0-S14-7a). L'UI relaie l'intention (campaignId d'URL + filtres
 * F) et habille. Le modèle 4 couleurs a été RETIRÉ (S14-bis).
 */
export interface OperationalViewClient {
  /**
   * (V-DASH — Tableau de bord macroscopique A-02) Synthèse + registre enrichi + criticité, UN round-trip.
   * PAS de campaignId (vue globale cross-campagnes). Autorité = API (criticité dérivée de computeAlerts).
   */
  getCampaignsDashboard(): Promise<CampaignsDashboardResponseDto>;

  /** (§2.5 — Suivi temps réel A4) Santé/alertes de pilotage (juxtaposition KPI + alertes composite). */
  getHealth(
    campaignId: string,
    filters: OperationalViewFilters,
  ): Promise<CampaignHealthDto>;

  /** (§2.5 v2.2 — Section 1) Cards communes Profils & Fournisseurs. */
  getCards(
    campaignId: string,
    filters: OperationalViewFilters,
  ): Promise<OperationalCardsDto>;

  /** (§2.5 v2.2 — Page Profils §2) Matrice Profil × Niveau (stats + taux + # Hors Jeux / # > P75). */
  getProfileMatrix(
    campaignId: string,
    filters: OperationalViewFilters,
  ): Promise<ProfileMatrixResponseDto>;

  /** (§2.5 v2.2 — Page Fournisseurs §2) Liste fournisseurs (raison sociale + taux + bandes + date). */
  getSupplierList(
    campaignId: string,
    filters: OperationalViewFilters,
  ): Promise<SupplierListResponseDto>;

  /** (§2.5 v2.2 — Page Profils §3) Détail d'un couple (par fournisseur : C1/C2 + écarts + évolution). */
  getProfileDetail(
    campaignId: string,
    filters: OperationalViewFilters,
  ): Promise<ProfileDetailResponseDto>;

  /** (§2.5 v2.2 — Page Fournisseurs §3) Détail d'un fournisseur (par couple : C1/C2 + écarts + évolution). */
  getSupplierDetail(
    campaignId: string,
    filters: OperationalViewFilters,
  ): Promise<SupplierDetailResponseDto>;
}
